import { GraphQLError } from '@pylot-data/graphqlFetch'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type { Cart, CartItemInterface, Maybe } from '@pylot-data/pylotschema'
import { updateCartItemsMutation as updateCartItemsMutationCheckout } from '@corratech/pylot-cart-manager/src/graphql/updateCartItemsMutation'
import { updateCartItemsMutation } from '@components/alfakher/pylot-cart-manager/src/graphql/updateCartItemsMutation'
import { CartData, useCart } from '@corratech/pylot-cart-manager/src/use-cart'
import { getIsSignedIn } from '@corratech/pylot-cart-manager/src/utils/getIsSignedIn'
import { useUI } from '@corratech/pylot-ui/context'

export type CartItemUpdateInput = {
    cart_item_uid: string
    quantity: number
}

export type UpdateCartItemsInput = {
    input: {
        cart_id: string
        cart_items: CartItemUpdateInput[]
        is_checkout: boolean
    }
    isSignedIn: boolean
}

export type UpdateCartItemsOutput = {
    updateCartItems: {
        cart: Cart
    }
    errors?: GraphQLError[]
}

const getOptimisticData = (
    cartData: CartData,
    cartItems: CartItemUpdateInput[]
): CartData => {
    const items = [...(cartData.data.cart.items || [])]
    const updatedItems = items!.reduce<Maybe<CartItemInterface[]>>(
        (newItems, item) => {
            const correspondingItemToUpdate = cartItems.find(
                (updatedItem) => updatedItem.cart_item_uid === item?.uid!
            )

            if (!correspondingItemToUpdate) {
                // @ts-ignore
                return [...newItems, item]
            }

            if (correspondingItemToUpdate.quantity === 0) {
                return newItems
            }

            return [
                ...newItems,
                {
                    ...item,
                    quantity: correspondingItemToUpdate.quantity
                }
            ]
        },
        []
    )

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items: updatedItems
            },
            isOptimistic: true
        }
    }
}

const formatData = (
    output: GraphqlFetchOutput<UpdateCartItemsOutput>
): CartData => {
    return {
        data: {
            cart: output.data.updateCartItems?.cart ?? null
        },
        errors: output.errors
    }
}

export type UpdateCartItems = (cartItems: CartItemUpdateInput[]) => void

export interface UseUpdateCartItemsReturn {
    updateCartItems: UpdateCartItems
    errorMessage?: string
}

const DEBOUNCE_TIME = 1000

export const useUpdateCartItems = (): UseUpdateCartItemsReturn => {
    const { locale, route } = useRouter()
    const { data, mutate } = useCart()
    const { setMinicartLoading } = useUI()
    const [errorMessage, setErrorMessage] = useState('')
    // Use `useCallback` to make sure that `updateCart` stays the same.
    // eslint-disable-next-line
    const updateCart = useCallback(
        // debounce to optimize number of requests sent to the server
        debounce(async (cartItems) => {
            setErrorMessage('')
            // update cart items, await for the response
            const response = await graphqlFetch<
                UpdateCartItemsInput,
                UpdateCartItemsOutput
            >({
                query:
                    route === '/checkout'
                        ? updateCartItemsMutationCheckout
                        : updateCartItemsMutation,
                variables: {
                    input: {
                        cart_id: String(data!.data.cart.id!),
                        cart_items: cartItems,
                        is_checkout: route === '/checkout'
                    },
                    isSignedIn: getIsSignedIn()
                },
                locale
            })
            setMinicartLoading(false)
            // if response contains errors
            if (response?.errors?.length) {
                // Leave the old cart data untouched, but indicate, that error has occurred.
                // And trigger the revalidation to get latest cart data with no errors
                setErrorMessage(response.errors[0].message)
                return mutate((oldData) => ({
                    data: oldData!.data,
                    errors: response.errors
                }))
            }

            // Otherwise update internal cache with the fresh data after updating items.
            // In this case, revalidation is not necessary, as data is fresh.
            mutate(formatData(response), false)
        }, DEBOUNCE_TIME),
        [data!.data?.cart?.id]
    )

    return {
        updateCartItems: useCallback(
            (cartItems: CartItemUpdateInput[]) => {
                setMinicartLoading(true)
                // trigger network request with actual mutation and update cart data
                updateCart(cartItems)
            },
            [data, mutate, updateCart]
        ),
        errorMessage
    }
}
